import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { Breadcrumbs } from '../components/shared/Breadcrumbs';
import {
  localityBySlug,
  shireForLocality,
  type LocalityEntry,
} from '../data/wb/localities';
import { closestServicesFor, type ClosestServices } from '../data/wb/locality-nearest';
import type { Shire } from '../components/shared/electorate-shires';

export const Route = createFileRoute('/locality/$name')({
  component: LocalityPage,
  loader: ({ params }) => {
    const entry = localityBySlug(params.name);
    if (!entry) throw notFound();
    const shire = shireForLocality(entry);
    const closest = closestServicesFor(entry);
    return { entry, shire, closest };
  },
  head: ({ loaderData }) => {
    const entry = loaderData?.entry;
    if (!entry) {
      return { meta: [{ title: 'Locality — Warren-Blackwood' }] };
    }
    const title = `${entry.name} (${entry.postcode}) — Shire of ${entry.shireName}`;
    const description =
      entry.blurb ??
      `${entry.name} is a locality in the Shire of ${entry.shireName}, postcode ${entry.postcode}, in WA's Warren-Blackwood electorate.`;
    return {
      meta: [
        { title: `${title} | Warren-Blackwood` },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
      ],
    };
  },
  notFoundComponent: LocalityNotFound,
});

const card: React.CSSProperties = {
  background: 'var(--cream)',
  border: '1px solid var(--rule)',
  borderRadius: 8,
  padding: '1.1rem 1.2rem',
  marginBottom: 'var(--sp-md)',
};

const h2: React.CSSProperties = {
  fontFamily: 'var(--font-heading)',
  fontSize: '1.15rem',
  color: 'var(--green)',
  margin: '0 0 0.7rem',
};

const link: React.CSSProperties = {
  color: 'var(--rivergum)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

function LocalityPage() {
  const { entry, shire, closest } = Route.useLoaderData() as {
    entry: LocalityEntry;
    shire: Shire | undefined;
    closest: ClosestServices;
  };

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <Breadcrumbs
        items={[
          { label: 'Map', to: '/' },
          { label: `Shire of ${entry.shireName}`, to: '/shire/$name', params: { name: entry.shireId } },
          { label: entry.name },
        ]}
      />

      <p
        style={{
          fontFamily: 'var(--font-sub)',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.72rem',
          color: 'var(--rivergum)',
          margin: '0.2rem 0 0.35rem',
        }}
      >
        Locality · Postcode {entry.postcode}
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(1.9rem, 5vw, 2.4rem)',
          color: 'var(--green)',
          margin: '0 0 0.4rem',
          lineHeight: 1.1,
        }}
      >
        {entry.name}
      </h1>
      <p style={{ color: 'var(--ink-mid)', margin: '0 0 var(--sp-md)', fontSize: '1rem' }}>
        In the{' '}
        <Link
          to="/shire/$name"
          params={{ name: entry.shireId }}
          style={{ ...link, fontWeight: 600 }}
        >
          Shire of {entry.shireName}
        </Link>
        , Warren-Blackwood electorate.
      </p>

      {entry.blurb && (
        <p
          style={{
            fontSize: '1.05rem',
            lineHeight: 1.55,
            color: 'var(--ink)',
            background: 'var(--green-pale)',
            borderLeft: '3px solid var(--green)',
            padding: '0.85rem 1rem',
            borderRadius: 4,
            margin: '0 0 var(--sp-md)',
          }}
        >
          {entry.blurb}
        </p>
      )}

      <section aria-labelledby="facts" style={card}>
        <h2 id="facts" style={h2}>The basics</h2>
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: '1rem', rowGap: '0.45rem', fontSize: '0.97rem' }}>
          <dt style={{ color: 'var(--ink-mid)' }}>Postcode</dt>
          <dd style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>{entry.postcode}</dd>

          <dt style={{ color: 'var(--ink-mid)' }}>Shire</dt>
          <dd style={{ margin: 0 }}>
            <Link to="/shire/$name" params={{ name: entry.shireId }} style={link}>
              Shire of {entry.shireName}
            </Link>
          </dd>

          <dt style={{ color: 'var(--ink-mid)' }}>State electorate</dt>
          <dd style={{ margin: 0 }}>Warren-Blackwood (Legislative Assembly)</dd>
        </dl>
      </section>

      {shire && (
        <CouncilLinks shire={shire} localityName={entry.name} />
      )}

      <ClosestServicesSection closest={closest} localityName={entry.name} />


      <section aria-labelledby="mp" style={card}>
        <h2 id="mp" style={h2}>Your state MP</h2>
        <p style={{ margin: '0 0 0.5rem', fontSize: '0.97rem', lineHeight: 1.5 }}>
          For matters handled by WA state government — hospitals, state schools,
          Main Roads, WA Police, housing — contact Bevan Eatts MP.
        </p>
        <p style={{ margin: 0 }}>
          <Link
            to="/contact"
            search={shire ? { from: shire.id } : {}}
            style={link}
          >
            Contact Bevan Eatts MP →
          </Link>
        </p>
      </section>
    </main>
  );
}

function CouncilLinks({ shire, localityName }: { shire: Shire; localityName: string }) {
  const ql = shire.quickLinks;
  const rows: { key: string; label: string; help: string; href?: string }[] = [
    {
      key: 'rates',
      label: 'Pay rates',
      help: `Pay or query your ${localityName} rates with the Shire of ${shire.name}.`,
      href: ql?.rates,
    },
    {
      key: 'waste',
      label: 'Bin day & waste',
      help: 'Bin collection schedule, recycling, and verge pickups.',
      href: ql?.waste,
    },
    {
      key: 'report',
      label: 'Report a road or local issue',
      help: 'Report damaged roads, broken streetlights, or other local concerns.',
      href: ql?.report,
    },
    {
      key: 'planning',
      label: 'Building & planning',
      help: 'Planning approvals, building permits, and development applications.',
      href: ql?.planning,
    },
  ].filter((r) => Boolean(r.href));

  if (rows.length === 0) return null;

  return (
    <section aria-labelledby="council" style={card}>
      <h2 id="council" style={h2}>Council services for {localityName}</h2>
      <p style={{ margin: '0 0 0.85rem', fontSize: '0.95rem', color: 'var(--ink-mid)', lineHeight: 1.5 }}>
        Day-to-day services — rates, bins, roads, planning — are run by the{' '}
        <Link to="/shire/$name" params={{ name: shire.id }} style={link}>
          Shire of {shire.name}
        </Link>
        .
      </p>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.6rem' }}>
        {rows.map((r) => (
          <li key={r.key}>
            <a
              href={r.href}
              style={{
                display: 'block',
                padding: '0.7rem 0.85rem',
                background: 'white',
                border: '1px solid var(--rule)',
                borderRadius: 6,
                color: 'var(--ink)',
                textDecoration: 'none',
              }}
            >
              <strong style={{ color: 'var(--green)' }}>{r.label} ↗</strong>
              <div style={{ fontSize: '0.88rem', color: 'var(--ink-mid)', marginTop: 2, lineHeight: 1.4 }}>
                {r.help}
              </div>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function fmtKm(km: number): string {
  if (km < 1) return '<1 km';
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

function ClosestServicesSection({
  closest,
  localityName,
}: {
  closest: ClosestServices;
  localityName: string;
}) {
  if (!closest.origin) return null;
  const { nearestTown, nearestHospital, nearestSchool } = closest;
  if (!nearestTown && !nearestHospital && !nearestSchool) return null;

  const rowStyle: React.CSSProperties = {
    padding: '0.75rem 0',
    borderTop: '1px solid var(--rule)',
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-sub)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.7rem',
    color: 'var(--rivergum)',
    margin: '0 0 0.2rem',
  };
  const distStyle: React.CSSProperties = {
    color: 'var(--ink-mid)',
    fontSize: '0.85rem',
    fontVariantNumeric: 'tabular-nums',
    marginLeft: '0.5rem',
  };

  return (
    <section aria-labelledby="closest" style={card}>
      <h2 id="closest" style={h2}>Closest services to {localityName}</h2>
      <p style={{ margin: '0 0 0.2rem', fontSize: '0.92rem', color: 'var(--ink-mid)', lineHeight: 1.5 }}>
        Straight-line distances from the centre of {localityName}. Driving
        distances will be longer.
      </p>

      {nearestTown && (
        <div style={rowStyle}>
          <p style={labelStyle}>Nearest service town</p>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            {nearestTown.slug ? (
              <Link to="/locality/$name" params={{ name: nearestTown.slug }} style={{ ...link, fontWeight: 600 }}>
                {nearestTown.name}
              </Link>
            ) : (
              <strong>{nearestTown.name}</strong>
            )}
            <span style={distStyle}>{fmtKm(nearestTown.distanceKm)} away</span>
          </p>
        </div>
      )}

      {nearestHospital && (
        <div style={rowStyle}>
          <p style={labelStyle}>Closest hospital / health service</p>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            <a href={nearestHospital.hospital.url} style={{ ...link, fontWeight: 600 }}>
              {nearestHospital.hospital.name} ↗
            </a>
            {!nearestHospital.inSameLocality && (
              <span style={distStyle}>
                in {nearestHospital.hospital.town} · {fmtKm(nearestHospital.distanceKm)} away
              </span>
            )}
            {nearestHospital.inSameLocality && (
              <span style={distStyle}>in {localityName}</span>
            )}
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.88rem', color: 'var(--ink-mid)', lineHeight: 1.45 }}>
            {nearestHospital.hospital.level}.
          </p>
        </div>
      )}

      {nearestSchool && (
        <div style={rowStyle}>
          <p style={labelStyle}>Closest school</p>
          <p style={{ margin: 0, fontSize: '1rem' }}>
            <a href={nearestSchool.school.websiteUrl} style={{ ...link, fontWeight: 600 }}>
              {nearestSchool.school.name} ↗
            </a>
            <span style={distStyle}>
              {nearestSchool.inSameLocality ? `in ${localityName}` : `in ${nearestSchool.school.locality} · ${fmtKm(nearestSchool.distanceKm)} away`}
            </span>
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.88rem', color: 'var(--ink-mid)', lineHeight: 1.45 }}>
            {nearestSchool.school.sector} · {nearestSchool.school.level} ({nearestSchool.school.yearRange})
          </p>
        </div>
      )}

      <p style={{ margin: '0.9rem 0 0', fontSize: '0.78rem', color: 'var(--ink-mid)', lineHeight: 1.45 }}>
        Hospitals from WA Country Health Service. Schools from the office's 2026 schools dataset. Distances are great-circle from locality centroids.
      </p>
    </section>
  );
}

function LocalityNotFound() {
  return (
    <main
      id="main-content"
      style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: 'var(--sp-xl) var(--sp-md)',
        textAlign: 'center',
      }}
    >
      <h1 style={{ fontFamily: 'var(--font-heading)', color: 'var(--green)' }}>
        Locality not found
      </h1>
      <p style={{ color: 'var(--ink-mid)', margin: '0.5rem 0 1.2rem' }}>
        We don't have a page for that locality. Try the map or the search bar
        on the home page.
      </p>
      <Link
        to="/"
        style={{
          display: 'inline-block',
          padding: '0.6rem 1rem',
          background: 'var(--green)',
          color: 'var(--cream)',
          borderRadius: 6,
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
        Back to the electorate map
      </Link>
    </main>
  );
}
